import { useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Play, X, ShieldCheck, GraduationCap, MessagesSquare, ArrowRight } from "lucide-react";
import { scrollToSection } from "./Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?crop=entropy&cs=srgb&fm=jpg&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="accueil" ref={ref} className="relative min-h-screen flex items-end overflow-hidden grain" data-testid="hero-section">
      <motion.div
        className="absolute inset-0"
        animate={{ scale: [1, 1.08] }}
        transition={{ duration: 18, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
      >
        <img src={HERO_IMG} alt="Ambiance détermination et dépassement de soi" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/70 to-obsidian/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />

      <motion.div style={{ y: yContent, opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-16 sm:pb-24 w-full">
        <h1 data-testid="hero-headline" className="font-heading font-black uppercase tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskedLine delay={0.25}>Une application de</MaskedLine>
          <MaskedLine delay={0.38}>
            préparation <span className="text-ember">mentale</span>
          </MaskedLine>
          <MaskedLine delay={0.51}>pour chaque apprenant.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          Réalisée par un professeur et adaptée aux objectifs de chacun, avec un suivi en
          visio pour donner un cap aux apprenants et développer les compétences de chacun.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={(e) => { e.preventDefault(); scrollToSection("#contact"); }}
            className="group inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberdark px-7 py-4 text-sm font-bold uppercase tracking-wide transition-colors duration-200"
          >
            Échanger avec Yassine (offert)
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-video"
            onClick={() => setVideoOpen(true)}
            className="group inline-flex items-center gap-3 rounded-full border border-white/25 hover:border-gold px-6 py-4 text-sm font-bold uppercase tracking-wide text-slate-100 transition-colors duration-200"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold text-obsidian transition-transform duration-200 group-hover:scale-110">
              <Play size={14} fill="currentColor" />
            </span>
            Voir l'immersion
          </button>
        </motion.div>

        <motion.a
          href="/test"
          data-testid="hero-cta-test"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-slate-100 hover:text-gold underline underline-offset-4 decoration-white/30 transition-colors duration-200"
        >
          Faire le test gratuit : quel est votre prochain cap ?
          <ArrowRight size={15} />
        </motion.a>

        <motion.a
          href="https://boussole.yassineabou.fr"
          target="_blank"
          rel="noreferrer"
          data-testid="hero-cta-boussole"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-amber-300 underline underline-offset-4 decoration-gold/40 transition-colors duration-200"
        >
          Boussole, l'application de préparation mentale, est en ligne — la découvrir
          <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
        </motion.a>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
        >
          {[
            { icon: MessagesSquare, text: "Communication, écoute & management" },
            { icon: GraduationCap, text: "Professeur & formateur depuis 10 ans" },
            { icon: ShieldCheck, text: "Cadre bienveillant & confidentiel" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-2.5 text-sm text-slate-300">
              <Icon size={17} className="text-gold" />
              <span>{text}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {videoOpen && (
          <motion.div
            data-testid="video-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian/90 backdrop-blur-md p-4"
            onClick={() => setVideoOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl rounded-2xl overflow-hidden border border-white/15 bg-coal shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative aspect-video overflow-hidden">
                <motion.img
                  src={HERO_IMG}
                  alt="Immersion — ambiance détermination"
                  className="w-full h-full object-cover"
                  animate={{ scale: [1, 1.1] }}
                  transition={{ duration: 10, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8">
                  <p className="font-heading text-2xl sm:text-3xl font-extrabold uppercase">Immersion</p>
                  <p className="mt-2 text-sm text-slate-300 max-w-lg">
                    La vidéo d'accroche (8 s, ambiance mental fort) sera intégrée ici dès réception du fichier final.
                  </p>
                </div>
              </div>
              <button
                data-testid="video-modal-close"
                onClick={() => setVideoOpen(false)}
                className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-full bg-obsidian/70 border border-white/20 hover:bg-ember transition-colors duration-200"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Hero;
